import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  AccessTokenResponse,
  FinanceApiService,
  LoginRequest,
  LogoutService,
  RemovePushNotificationCommand,
  UserInfoDto,
  UsersService,
} from 'api/openapi';
import useApi from 'api/mavapi/useApi';
import Cookies from 'js-cookie';
import { useAccessToken } from './useAccessToken';
import { useOnErrorCallBack } from './useErrorHandling';
import React from 'react';
import { useNavigateToRoute } from 'utils/common';
import { useNotificationApi } from './useNotificationApi';
import usePushNotifications from './usePushNotifications';
import { setRequestHeaders } from 'utils/tokenManager';

const useLoginApi = () => {
  const { handleRequest } = useApi();
  const { onErrorCallBack } = useOnErrorCallBack();
  const { tokenFound, currentNotificationToken } = usePushNotifications();
  const [isLoading, setIsLoading] = React.useState(false);
  const redirect = useNavigateToRoute();
  const queryClient = useQueryClient();
  const { useSendNotificationToken } = useNotificationApi();
  const useLogin = (email: string, password: string) => {
    const { handleRequest } = useApi();
    const { postAuthLogin } = FinanceApiService;
    const accessToken = useAccessToken();

    return useQuery<AccessTokenResponse>({
      queryKey: ['loginAPI', email],
      queryFn: async () => {
        const loginRequest: LoginRequest = { email, password };
        const response = await handleRequest(
          postAuthLogin(false, false, loginRequest),
        );
        Cookies.set('access_token', response.accessToken);
        Cookies.set('refreshToken', response.refreshToken);
        Cookies.set('tokenExpiry', response.expiresIn);
        return response;
      },
      enabled: !accessToken, // Only run the query if accessToken doesn't exist
    });
  };

  const useLoginApiForPage = (
    setFailureData,
  ): UseMutationResult<
    any,
    Error,
    {
      requestBody?: LoginRequest;
    }
  > => {
    const sendNotificationToken = useSendNotificationToken();
    return useMutation({
      mutationFn: async ({ requestBody }) => {
        setIsLoading(true);
        const response = await handleRequest(
          FinanceApiService.postAuthLogin(false, false, requestBody),
        );
        const expiryTime = new Date();
        expiryTime.setSeconds(
          expiryTime.getSeconds() + response.expiresIn - 300,
        );
        // Set tokens in cookies
        Cookies.set('access_token', response.accessToken);
        Cookies.set('refreshToken', response.refreshToken);
        Cookies.set('tokenExpiry', expiryTime.getTime());

        return { data: response };
      },
      onSuccess: async response => {
        try {
          // Small delay to ensure tokens are set
          await new Promise(resolve => setTimeout(resolve, 1000));
          // Invalidate any existing user details
          await queryClient.invalidateQueries({ queryKey: ['userDetails'] });
          await setRequestHeaders();
          // Fetch user profile with the new token
          const userResponse = await handleRequest(
            UsersService.getApiUsersLoggedInUserProfile(),
          );

          // Store user details securely
          Cookies.set('userDetails', JSON.stringify(userResponse), {
            secure: true,
            sameSite: 'strict',
          });

          // Update the query cache with the new user data
          queryClient.setQueryData(['userDetails'], userResponse);
          try {
            if (tokenFound && currentNotificationToken) {
              await sendNotificationToken.mutateAsync();
            }
          } catch (error) {
            console.error('Error sending notification token:', error);
            // Continue with login flow even if notification token fails
          }
          setIsLoading(false);
          redirect('/dashboard/facility');
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setIsLoading(false);
          // Handle the error appropriately, maybe show a warning that user details couldn't be fetched
        }
      },
      onError: error => {
        setFailureData();
        setIsLoading(false);
        onErrorCallBack(undefined, 'Username or Password is incorrect');
      },
    });
  };

  const useUserLoginApi = () => {
    const accessToken = useAccessToken();

    return useQuery<UserInfoDto>({
      queryKey: ['userDetails'],
      queryFn: async () => {
        try {
          const response = await handleRequest(
            UsersService.getApiUsersLoggedInUserProfile(),
          );

          // Store user details securely
          Cookies.set('userDetails', JSON.stringify(response), {
            secure: true,
            sameSite: 'strict',
          });

          return response;
        } catch (error) {
          console.error('Error fetching user profile:', error);
          throw error;
        }
      },
      enabled: Boolean(accessToken),
      retry: 1,
      staleTime: 5 * 60 * 1000,
    });
  };

  const useLogout = (
    requestBody: RemovePushNotificationCommand,
    handleRecall?: () => void,
  ) => {
    return useMutation<string, Error, void>({
      mutationFn: () => {
        const response = handleRequest(
          LogoutService.postApiLogoutLogoutToken(requestBody),
        );
        handleRecall?.();
        return response;
      },
    });
  };

  return {
    useLogin,
    useLoginApiForPage,
    isLoading,
    useUserLoginApi,
    useLogout,
  };
};

export default useLoginApi;
